import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Radio,
  DatePicker,
  Select,
  Space,
  message,
  Empty,
  Progress,
} from 'antd';
import {
  fetchRefundSummary,
  fetchRefundTrend,
  fetchRefundReasons,
  fetchTopRefundProducts,
} from '../../api/refundStats';

const { RangePicker } = DatePicker;
const { Option } = Select;

const refundStatusMap = {
  8: { text: '退款完成', color: 'green' },
  6: { text: '验收通过', color: 'blue' },
  2: { text: '审核拒绝', color: 'red' },
  9: { text: '已取消', color: 'default' },
};

const RefundStatistics = () => {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState({ trend: [], type: 'daily' });
  const [reasons, setReasons] = useState({ distribution: [], totalCount: 0 });
  const [topProducts, setTopProducts] = useState([]);
  const [period, setPeriod] = useState(30);
  const [trendType, setTrendType] = useState('daily');
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [summaryRes, trendRes, reasonsRes, topRes] = await Promise.all([
        fetchRefundSummary(),
        fetchRefundTrend({ type: trendType, period }),
        fetchRefundReasons(
          dateRange
            ? {
                start_date: dateRange[0].format('YYYY-MM-DD'),
                end_date: dateRange[1].format('YYYY-MM-DD'),
              }
            : {}
        ),
        fetchTopRefundProducts({ limit: 10 }),
      ]);
      if (summaryRes.success) setSummary(summaryRes.data);
      if (trendRes.success) setTrend(trendRes.data);
      if (reasonsRes.success) setReasons(reasonsRes.data);
      if (topRes.success) setTopProducts(topRes.data);
    } catch (error) {
      message.error('加载退款统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendType, period, dateRange]);

  const maxReasonCount = reasons.distribution.reduce(
    (max, r) => Math.max(max, Number(r.count) || 0),
    1
  );

  return (
    <div className="workspace-page">
      <div className="workspace-header">
        <div>
          <h1 className="workspace-title">退款统计</h1>
          <p className="workspace-description">退款趋势、原因分布与高频退款商品分析</p>
        </div>
        <Space>
          <Radio.Group
            value={trendType}
            onChange={(e) => setTrendType(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="daily">按日</Radio.Button>
            <Radio.Button value="monthly">按月</Radio.Button>
          </Radio.Group>
          <Select value={period} onChange={setPeriod} style={{ width: 110 }}>
            <Option value={7}>近7天</Option>
            <Option value={30}>近30天</Option>
            <Option value={90}>近90天</Option>
            <Option value={180}>近180天</Option>
          </Select>
          <RangePicker value={dateRange} onChange={setDateRange} />
        </Space>
      </div>

      {/* 总览卡片 */}
      <Row gutter={[16, 16]} className="workspace-summary">
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title="累计退款订单"
              value={summary?.totalRefundOrders || 0}
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title="累计退款金额"
              value={summary?.totalRefundAmount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#fa541c' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title="平均退款金额"
              value={summary?.avgRefundAmount || 0}
              precision={2}
              prefix="¥"
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading}>
            <Statistic
              title="售后申请总数"
              value={summary?.totalReturnRequests || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 趋势与原因分布 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card title="退款趋势" size="small">
            {trend.trend && trend.trend.length > 0 ? (
              <Table
                rowKey="date"
                size="small"
                pagination={false}
                scroll={{ y: 320 }}
                dataSource={trend.trend}
                columns={[
                  {
                    title: trendType === 'daily' ? '日期' : '月份',
                    dataIndex: 'date',
                  },
                  {
                    title: '退款订单数',
                    dataIndex: 'refundOrders',
                    align: 'right',
                    render: (v) => <Tag color="orange">{v}</Tag>,
                  },
                  {
                    title: '退款金额',
                    dataIndex: 'refundAmount',
                    align: 'right',
                    render: (v) => <span style={{ color: '#fa541c' }}>¥{Number(v).toFixed(2)}</span>,
                  },
                  {
                    title: '退款率',
                    dataIndex: 'refundRate',
                    align: 'right',
                    render: (v) => <span>{Number(v).toFixed(2)}%</span>,
                  },
                ]}
              />
            ) : (
              <Empty description="该时间段暂无退款数据" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card title="退款原因分布" size="small">
            {reasons.distribution && reasons.distribution.length > 0 ? (
              <div style={{ padding: '8px 0' }}>
                {reasons.distribution.map((item) => (
                  <div key={item.reason} style={{ marginBottom: 16 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: 4,
                      }}
                    >
                      <span>{item.reason}</span>
                      <span>
                        <Tag color="volcano">{item.count} 单</Tag>
                        <span style={{ fontSize: 12, color: '#999' }}>
                          ¥{Number(item.totalAmount || 0).toFixed(2)} ({Number(item.percent || 0).toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                    <Progress
                      percent={Number(
                        ((Number(item.count) / maxReasonCount) * 100).toFixed(1)
                      )}
                      showInfo={false}
                      strokeColor="#fa541c"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="暂无退款原因数据" />
            )}
          </Card>
        </Col>
      </Row>

      {/* 退款商品 Top */}
      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="高频退款商品 Top10" size="small">
            <Table
              rowKey="productId"
              size="small"
              pagination={false}
              dataSource={topProducts}
              columns={[
                {
                  title: '商品',
                  dataIndex: 'productName',
                  render: (name, record) => (
                    <Space>
                      {record.productImage && (
                        <img
                          src={record.productImage}
                          alt={name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <span>{name}</span>
                    </Space>
                  ),
                },
                {
                  title: '退款次数',
                  dataIndex: 'refundTimes',
                  align: 'right',
                  render: (v) => <Tag color="red">{v} 次</Tag>,
                },
                {
                  title: '退款数量',
                  dataIndex: 'refundQuantity',
                  align: 'right',
                },
                {
                  title: '退款金额',
                  dataIndex: 'refundAmount',
                  align: 'right',
                  render: (v) => (
                    <span style={{ color: '#fa541c', fontWeight: 'bold' }}>
                      ¥{Number(v).toFixed(2)}
                    </span>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RefundStatistics;
